<script>
  const siteName = "Indonesian with Devia";
  const siteUrl = "https://deviateaches.com";
  const pageTitle = "Indonesian with Devia | Learn Bahasa Indonesia Online";
  const pageDescription =
    "Learn Bahasa Indonesia with Devia, a local Indonesian tutor from Bali. Practical one-on-one lessons for conversation, slang, vocabulary, and real-life confidence.";
  const pageImage = `${siteUrl}/img/devia_headshot_3.jpeg`;
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Devia",
    jobTitle: "Bahasa Indonesia Tutor",
    url: siteUrl,
    description:
      "Local Indonesian tutor offering practical one-on-one Bahasa Indonesia lessons focused on real-life conversation, confidence, and slang.",
    image: pageImage,
    knowsAbout: [
      "Bahasa Indonesia",
      "Indonesian conversation",
      "Indonesian slang",
      "Language tutoring",
    ],
  };

  const learnCards = [
    {
      icon: "🗣️",
      title: "Everyday conversation",
      description:
        "Learn practical phrases for greetings, small talk, ordering food, and getting around with more ease.",
      accent: "#7f1f14",
    },
    {
      icon: "📚",
      title: "Useful local vocabulary",
      description:
        "Build the kind of vocabulary that people actually use in day-to-day situations in Indonesia.",
      accent: "#c98f86",
    },
    {
      icon: "🤩",
      title: "Natural confidence",
      description:
        "Practice speaking in a simple, encouraging way so you can respond faster and feel less nervous using the language.",
      accent: "#4d2f22",
    },
  ];

  const confidencePoints = [
    {
      emoji: "⏱️",
      title: "1000+ hours of teaching",
      detail:
        "Extensive one-on-one lesson time across beginner and intermediate students.",
    },
    {
      emoji: "🗣️",
      title: "4 years teaching Indonesian",
      detail:
        "Several years of focused experience helping students speak naturally and confidently.",
    },
    {
      emoji: "🌏",
      title: "50+ students from around the world",
      detail:
        "Students from the U.S., Australia, England, Ireland, Germany, France, Spain, Singapore, Japan, Ukraine, the UAE, Malaysia, and more.",
    },
    {
      emoji: "🎓",
      title: "Degree in Communication",
      detail: "A formal background in communication studies.",
    },
    {
      emoji: "🎭",
      title: "Specialized learner experience",
      detail:
        "Taught a professional actor preparing for an Indonesian-speaking role and a scientist working with technical vocabulary.",
    },
    {
      emoji: "🍜",
      title: "Useful Indonesian for real life",
      detail:
        "Learn how to order food, talk to drivers, shopkeepers, locals, and build confidence in everyday conversations.",
    },
    {
      emoji: "🧩",
      title: "Personalized study plan tailored to you",
      detail:
        "Lessons are shaped around your goals and interests so you can focus on the vocabulary, situations, and skills that matter the most to you.",
    },
  ];

  const reviews = [
    {
      quote:
        "Fantastic teacher. I've worked with language teachers in multiple languages and she is much better than others. More than 5 stars.",
      author: "Hamish",
    },
    {
      quote:
        "Devia is an exceptional teacher of Bahasa Indonesian. She would be an ideal teacher for anyone hoping to learn how to speak the language like a local. She is a highly educated and delightful young woman who puts a great deal of effort into educating her students. Her English is also outstandingly good. If I could give more than 5 stars I would.",
      author: "Sean",
    },
    {
      quote:
        "Devia is an incredible teacher; she is super patient, always well prepared and the lessons are always really fun and her personality shines through in every class. I started Bahasa Indonesia without knowing a single word and after just a few lessons I am really happy with my progress. Would definitely recommend Devia to anyone looking to learn Bahasa Indonesia!",
      author: "Matt",
    },
    {
      quote:
        "Devia is an amazing tutor, I already felt much more confident after only one lesson. Her English is outstanding, her lessons are well prepared and interesting, and she is so easy to talk to. Really recommend for anyone wanting to learn local Indonesian language as well as complex grammar!",
      author: "Asha",
    },
    {
      quote:
        "Devia is an outstanding Indonesian tutor. She's patient, relaxed, and genuinely fun to learn with. She made the language feel easy and accessible, and her sense of humour and warmth made every session something I actually looked forward to. What really stood out was the local insight she brought. She taught me things you simply can't get from textbooks or apps, and it made the language click in a completely different way. I'd highly recommend her to anyone wanting to learn Indonesian properly, and I'll be booking with her again whenever I need to practice.",
      author: "Rach",
    },
    {
      quote:
        "Devia is a great tutor, she has an excellent comprehension of English which I have found is the most essential skill when looking for a tutor. I wanted to speak more colloquially and improve my grammar and Devia's understanding of English grammar makes her a 5 star tutor in formal and informal teaching of language.",
      author: "Debbie",
    },
  ];

  const reviewCharLimit = 220;
  let reviewsCarousel;
  let expandedReviews = reviews.map(() => false);

  function isReviewTruncated(review) {
    return review.quote.length > reviewCharLimit;
  }

  function toggleReview(index) {
    if (expandedReviews[index]) {
      expandedReviews = reviews.map(() => false);
      return;
    }

    expandedReviews = reviews.map((_, reviewIndex) => reviewIndex === index);
  }

  function scrollReviews(direction) {
    if (!reviewsCarousel) return;

    const card = reviewsCarousel.querySelector(".review-card");
    const gap = 20;
    const offset = card
      ? card.clientWidth + gap
      : reviewsCarousel.clientWidth * 0.85;

    reviewsCarousel.scrollBy({
      left: direction * offset,
      behavior: "smooth",
    });
  }
</script>

<svelte:head>
  <title>{pageTitle}</title>
  <meta name="description" content={pageDescription} />
  <meta
    name="keywords"
    content="learn Bahasa Indonesia, Indonesian tutor, Bahasa Indonesia lessons, online Indonesian teacher, Indonesian conversation practice, learn Indonesian online"
  />
  <meta name="robots" content="index, follow, max-image-preview:large" />
  <meta name="author" content="Devia" />
  <link rel="canonical" href={siteUrl} />
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content={siteName} />
  <meta property="og:url" content={siteUrl} />
  <meta property="og:title" content={pageTitle} />
  <meta property="og:description" content={pageDescription} />
  <meta property="og:image" content={pageImage} />
  <meta property="og:locale" content="en_US" />
  <meta name="twitter:url" content={siteUrl} />
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={pageTitle} />
  <meta name="twitter:description" content={pageDescription} />
  <meta name="twitter:image" content={pageImage} />
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link
    href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap"
    rel="stylesheet"
  />
  <script type="application/ld+json">
    {JSON.stringify(structuredData)}
  </script>
</svelte:head>

<div class="page">
  <section class="hero">
    <h1>Learn Bahasa Indonesia</h1>
    <h2>in a fun, practical way</h2>
    <p class="subtitle">Even if you're a total beginner!</p>

    <a class="cta" href="https://wa.me/+6281239657139" target="_blank"
      >Start Learning</a
    >

    <div class="portrait-card">
      <div class="portrait">
        <!-- Replace this block with a real image when ready -->
        <img src="/img/devia_headshot.webp" alt="Portrait of Devia Pande" />
      </div>

      <div class="portrait-side-art">
        <div class="portrait-copy">
          <!-- <p class="portrait-kicker">Meet Your Teacher</p> -->
          <h3>Learn Bahasa Indonesia with a local 🇮🇩</h3>
          <p>
            Hi, I'm Devia from Bali. I offer fun, relaxed lessons to help you
            speak real Indonesian <i>- local slang included -</i> so you can sound
            natural and confident!
          </p>
        </div>
      </div>
    </div>
  </section>

  <section class="learn-section learn-section-secondary">
    <div class="section-header">
      <p class="section-kicker">What You'll Learn</p>
      <h2>Useful Indonesian for real life</h2>
    </div>

    <div class="learn-grid">
      {#each learnCards as card}
        <article class="learn-card">
          <div class="learn-icon">{card.icon}</div>
          <h3>{card.title}</h3>
          <p>{card.description}</p>
          <div class="learn-accent" style={`background: ${card.accent}`}></div>
        </article>
      {/each}
    </div>
  </section>

  <section class="confidence-section">
    <div class="section-header">
      <p class="section-kicker">Experience</p>
      <h2 class="confidence-title">Why learn with me?</h2>
    </div>

    <ul class="confidence-list">
      {#each confidencePoints as point}
        <li class="confidence-item">
          <div class="confidence-icon" aria-hidden="true">{point.emoji}</div>
          <div class="confidence-copy">
            <h3>{point.title}</h3>
            <p>{point.detail}</p>
          </div>
        </li>
      {/each}
    </ul>
  </section>

  <section class="reviews-section">
    <div class="section-header">
      <p class="section-kicker">Reviews</p>
      <h2>What students say</h2>
    </div>

    <div class="reviews-carousel-shell">
      <button
        class="carousel-button"
        type="button"
        aria-label="Scroll reviews left"
        on:click={() => scrollReviews(-1)}
      >
        ←
      </button>

      <div class="reviews-carousel" bind:this={reviewsCarousel}>
        {#each reviews as review, index}
          <article class="review-card">
            <div
              class="review-quote-area"
              class:expanded={expandedReviews[index]}
            >
              <p>
                "{expandedReviews[index] || !isReviewTruncated(review)
                  ? review.quote
                  : `${review.quote.slice(0, reviewCharLimit).trimEnd()}...`}"
              </p>
            </div>
            {#if isReviewTruncated(review)}
              <button
                class="review-toggle"
                type="button"
                aria-expanded={expandedReviews[index] ? "true" : "false"}
                on:click={() => toggleReview(index)}
              >
                {expandedReviews[index] ? "Show less" : "Show more"}
              </button>
            {/if}
            <span>— {review.author}</span>
          </article>
        {/each}
      </div>

      <button
        class="carousel-button"
        type="button"
        aria-label="Scroll reviews right"
        on:click={() => scrollReviews(1)}
      >
        →
      </button>
    </div>
  </section>

  <section class="final-cta-section">
    <div class="section-header">
      <!-- <p class="section-kicker">Start Now</p> -->
      <h2 class="final-cta-title">
        Ready to start learning <span>Bahasa Indonesia</span> with me?
      </h2>
    </div>

    <a class="cta final-cta" href="https://wa.me/+6281239657139" target="_blank"
      >Start Learning Now</a
    >
  </section>

  <section class="socials-section" id="contact">
    <div class="section-header">
      <p class="section-kicker">Contact</p>
      <h2>Let's connect</h2>
    </div>

    <div class="social-links">
      <a class="social-link" href="mailto:deviapandework@gmail.com">
        <span class="social-badge">✉️</span>
        <span>deviapandework@gmail.com</span>
      </a>

      <a
        class="social-link"
        href="https://www.facebook.com/profile.php?id=61574701910040"
        target="_blank"
        rel="noreferrer"
      >
        <span class="social-badge">f</span>
        <span>Facebook</span>
      </a>

      <a
        class="social-link"
        href="https://preply.com/en/tutor/1834073"
        target="_blank"
        rel="noreferrer"
      >
        <span class="social-badge" id="preply">P</span>
        <span>Preply</span>
      </a>

      <a class="social-link" href="https://wa.me/+6281239657139">
        <span class="social-badge" id="wa">WA</span>
        <span>Whatsapp</span>
      </a>
    </div>
  </section>
</div>

<style>
  :global(html, body) {
    margin: 0;
    padding: 0;
    min-height: 100%;
    background: #f5e8df;
  }

  :global(body) {
    font-family: "Poppins", "Avenir Next", "Nunito", "Segoe UI", "Trebuchet MS",
      sans-serif;
    color: #4f2b23;
  }

  :global(*) {
    box-sizing: border-box;
  }

  .page {
    position: relative;
    overflow: hidden;
    min-height: 100vh;
    background: linear-gradient(
      to bottom,
      #f7ebe3 0%,
      #f5e9e1 55%,
      #f6ece6 100%
    );
  }

  .hero,
  .learn-section,
  .confidence-section,
  .reviews-section,
  .final-cta-section,
  .socials-section {
    width: min(900px, calc(100% - 32px));
    margin: 0 auto;
    position: relative;
    z-index: 1;
  }

  .final-cta-title span {
    font-weight: 700;
  }

  .hero {
    padding-top: 64px;
    text-align: center;
  }

  .hero h1 {
    margin: 0;
    font-size: clamp(2.7rem, 6vw, 4.9rem);
    font-weight: 500;
    line-height: 1.08;
    color: #4d2a22;
    text-wrap: balance;
    font-family: "Poppins", "Avenir Next", "Nunito", "Segoe UI", "Trebuchet MS",
      sans-serif;
  }

  .hero h2 {
    margin: 8px 0 0;
    font-size: clamp(2.2rem, 5vw, 3.9rem);
    font-weight: 500;
    line-height: 1.12;
    color: #4d2a22;
    text-wrap: balance;
    font-family: "Poppins", "Avenir Next", "Nunito", "Segoe UI", "Trebuchet MS",
      sans-serif;
  }

  .subtitle {
    margin: 32px 0 0;
    font-size: clamp(1.2rem, 2.5vw, 1.9rem);
    font-family: inherit;
    color: #5f342b;
    text-wrap: pretty;
  }

  .cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 34px;
    padding: 18px 38px;
    min-width: 280px;
    border-radius: 22px;
    text-decoration: none;
    font-size: 1.2rem;
    letter-spacing: 0.08em;
    color: #fff4ee;
    background: #006abb;
    transition:
      transform 0.32s ease,
      box-shadow 0.32s ease,
      background-color 0.32s ease;
    font-weight: 100;
  }

  .cta:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 30px rgba(79, 37, 29, 0.3);
    background: #2297f1;
  }

  .portrait-card {
    margin-top: 68px;
    display: grid;
    grid-template-columns: 1.05fr 0.95fr;
    min-height: 410px;
    position: relative;
    border-radius: 26px;
    overflow: hidden;
    background: rgba(255, 248, 243, 0.58);
    box-shadow: 0 18px 40px rgba(112, 77, 63, 0.12);
  }

  .portrait {
    position: absolute;
    inset: 0 auto 0 -8%;
    width: 72%;
    z-index: 2;
  }

  .portrait :global(img) {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    mask-image: linear-gradient(
      90deg,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 1) 72%,
      rgba(0, 0, 0, 0.72) 84%,
      rgba(0, 0, 0, 0.18) 93%,
      transparent 100%
    );
    -webkit-mask-image: linear-gradient(
      90deg,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 1) 72%,
      rgba(0, 0, 0, 0.72) 84%,
      rgba(0, 0, 0, 0.18) 93%,
      transparent 100%
    );
  }

  .portrait-side-art {
    position: relative;
    grid-column: 2;
    background: #f5e7df;
    min-height: 410px;
  }

  .portrait-copy {
    position: relative;
    z-index: 1;
    max-width: 20rem;
    margin-left: auto;
    padding: 3rem 2.25rem 3rem 1rem;
    display: flex;
    min-height: 100%;
    flex-direction: column;
    justify-content: center;
    color: #5a3328;
  }

  .portrait-kicker {
    margin: 0 0 0.7rem;
    font-size: 0.78rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #8b4b3d;
  }

  .portrait-copy h3 {
    margin: 0 0 0.9rem;
    font-size: clamp(1.4rem, 2.2vw, 1.9rem);
    line-height: 1.15;
    color: #4d2a22;
    font-weight: 600;
    text-wrap: balance;
  }

  .portrait-copy p:last-child {
    margin: 0;
    font-size: 1rem;
    line-height: 1.7;
    color: #6a4639;
    text-wrap: pretty;
  }

  .learn-section {
    padding-top: 56px;
  }

  .learn-section-secondary {
    padding-top: 76px;
  }

  .learn-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 18px;
  }

  .learn-card {
    position: relative;
    padding: 22px 22px 28px;
    border-radius: 22px;
    background: rgba(255, 249, 245, 0.76);
    box-shadow: 0 10px 24px rgba(111, 67, 55, 0.08);
    overflow: hidden;
  }

  .learn-icon {
    width: 56px;
    height: 56px;
    border-radius: 18px;
    display: grid;
    place-items: center;
    margin-bottom: 18px;
    background: rgba(255, 255, 255, 0.72);
    font-size: 1.6rem;
    line-height: 1;
  }

  .learn-card h3 {
    margin: 0 0 10px;
    font-size: 1.15rem;
    line-height: 1.25;
    color: #4f2a22;
    font-weight: 700;
    text-wrap: balance;
    font-family: "Poppins", "Avenir Next", "Nunito", "Segoe UI", "Trebuchet MS",
      sans-serif;
  }

  .learn-card p {
    margin: 0;
    font-size: 1rem;
    line-height: 1.6;
    color: #5f342b;
    font-family: inherit;
    text-wrap: pretty;
  }

  .learn-accent {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 8px;
  }

  .confidence-section {
    padding-top: 70px;
  }

  .confidence-title {
    margin: 0;
    font-size: clamp(2rem, 4.4vw, 3.5rem);
    line-height: 1.18;
    font-weight: 500;
    color: #4f2a22;
    text-wrap: balance;
  }

  .confidence-list {
    margin: 28px 0 0;
    padding: 0;
    list-style: none;
    display: grid;
    gap: 18px;
  }

  .confidence-item {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 18px;
    align-items: start;
    padding: 22px 24px;
    border-radius: 22px;
    background: rgba(255, 249, 245, 0.76);
    box-shadow: 0 10px 24px rgba(111, 67, 55, 0.08);
  }

  .confidence-icon {
    width: 52px;
    height: 52px;
    border-radius: 16px;
    display: grid;
    place-items: center;
    background: rgba(255, 255, 255, 0.82);
    font-size: 1.5rem;
    line-height: 1;
  }

  .confidence-copy h3 {
    margin: 0 0 8px;
    font-size: 1.15rem;
    line-height: 1.3;
    color: #4f2a22;
    font-weight: 700;
    text-wrap: balance;
    font-family: "Poppins", "Avenir Next", "Nunito", "Segoe UI", "Trebuchet MS",
      sans-serif;
  }

  .confidence-copy p {
    margin: 0;
    font-size: 1rem;
    line-height: 1.65;
    color: #5f342b;
    font-family: inherit;
    text-wrap: pretty;
  }

  .reviews-section {
    width: min(1120px, calc(100% - 32px));
    padding-top: 76px;
  }

  .final-cta-section {
    padding-top: 76px;
    text-align: center;
  }

  .final-cta {
    margin-top: 10px;
  }

  .socials-section {
    padding: 76px 0 100px;
  }

  .section-header {
    text-align: center;
    margin-bottom: 28px;
  }

  .section-kicker {
    margin: 0 0 10px;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    font-size: 1rem;
    color: #6f3d31;
    font-family: inherit;
  }
  .section-header h2 {
    margin: 0;
    font-size: clamp(1.9rem, 4vw, 3rem);
    font-weight: 500;
    color: #4f2a22;
    text-wrap: balance;
    font-family: "Poppins", "Avenir Next", "Nunito", "Segoe UI", "Trebuchet MS",
      sans-serif;
  }

  .reviews-carousel-shell {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 16px;
    align-items: center;
  }

  .reviews-carousel {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    overflow-x: auto;
    padding: 8px 4px 18px;
    scroll-snap-type: x mandatory;
    scrollbar-width: thin;
    scrollbar-color: rgba(109, 61, 48, 0.35) transparent;
  }

  .reviews-carousel::-webkit-scrollbar {
    height: 10px;
  }

  .reviews-carousel::-webkit-scrollbar-track {
    background: transparent;
  }

  .reviews-carousel::-webkit-scrollbar-thumb {
    background: rgba(109, 61, 48, 0.35);
    border-radius: 999px;
  }

  .carousel-button {
    width: 48px;
    height: 48px;
    border: 0;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: rgba(255, 249, 245, 0.92);
    color: #4f2a22;
    box-shadow: 0 10px 24px rgba(111, 67, 55, 0.12);
    font: inherit;
    font-size: 1.3rem;
    cursor: pointer;
    transition:
      transform 160ms ease,
      background 160ms ease;
  }

  .carousel-button:hover {
    background: #fff7f1;
    transform: translateY(-1px);
  }

  .review-card {
    display: flex;
    flex: 0 0 calc((100% - 40px) / 3);
    flex-direction: column;
    height: 400px;
    padding: 22px;
    border-radius: 20px;
    background: rgba(255, 249, 245, 0.72);
    box-shadow: 0 10px 24px rgba(111, 67, 55, 0.08);
    scroll-snap-align: start;
    min-height: 400px;
  }

  .review-quote-area {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    margin-bottom: 14px;
  }

  .review-quote-area.expanded {
    overflow-y: auto;
    padding-right: 6px;
  }

  .review-card p {
    margin: 0;
    font-size: 1rem;
    line-height: 1.65;
    color: #593127;
    font-family: inherit;
    text-wrap: pretty;
  }

  .review-card span {
    margin-top: auto;
    font-size: 0.95rem;
    color: #6d3d30;
    font-family: inherit;
  }

  .review-toggle {
    width: fit-content;
    margin: 0 0 16px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #7f1f14;
    font: inherit;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
  }

  .review-toggle:hover {
    text-decoration: underline;
  }

  .social-links {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  }

  .social-link {
    width: fit-content;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 22px;
    border-radius: 999px;
    text-decoration: none;
    color: #4f2a22;
    background: rgba(255, 249, 245, 0.72);
    box-shadow: 0 10px 24px rgba(111, 67, 55, 0.08);
    font-family: inherit;
    font-weight: 600;
    text-wrap: pretty;
  }

  .social-badge {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: #3b5998;
    color: #fff;
    font-weight: 700;
    font-size: 1.1rem;
  }

  #wa {
    background: #25d366;
  }

  #preply {
    background: #ff79ac;
  }
  @media (max-width: 820px) {
    .portrait-card {
      grid-template-columns: 1fr;
    }

    .portrait {
      position: relative;
      inset: auto;
      width: 100%;
      min-height: 320px;
    }

    .portrait :global(img) {
      mask-image: none;
      -webkit-mask-image: none;
    }

    .portrait-side-art {
      grid-column: 1;
      min-height: 180px;
    }

    .portrait-copy {
      max-width: none;
      margin-left: 0;
      padding: 1.5rem;
    }

    .learn-grid {
      grid-template-columns: 1fr;
      gap: 24px;
    }

    .confidence-item {
      grid-template-columns: auto 1fr;
      gap: 16px;
    }

    .reviews-carousel {
      align-items: flex-start;
    }

    .review-card {
      flex-basis: calc((100% - 20px) / 2);
      height: 380px;
      min-height: 380px;
    }
  }

  @media (max-width: 560px) {
    .hero {
      padding-top: 42px;
    }

    .cta {
      min-width: 0;
      width: 100%;
      padding-left: 24px;
      padding-right: 24px;
    }

    .portrait-card {
      margin-top: 42px;
      min-height: 0;
    }

    .portrait-placeholder,
    .portrait :global(img) {
      min-height: 300px;
    }

    .confidence-section,
    .reviews-section,
    .final-cta-section,
    .socials-section {
      padding-top: 56px;
    }

    .reviews-carousel-shell {
      grid-template-columns: 1fr;
    }

    .carousel-button {
      display: none;
    }

    .reviews-carousel {
      padding-bottom: 14px;
    }

    .review-card {
      flex-basis: 88%;
      height: 360px;
      min-height: 360px;
    }

    .confidence-item {
      padding: 20px;
    }

    .confidence-icon {
      width: 46px;
      height: 46px;
      border-radius: 14px;
      font-size: 1.35rem;
    }
  }
</style>
